/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Divider, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import {
  Application,
  ApplicationEr,
  ApplicationEv,
  ApplicationStatus,
  ApplicationType,
  IneligibilityReasonCode,
  JobStatus,
  QuoteStatus,
  RequestCategory,
  RequestStatus,
  Request,
  ContactStatus,
} from '../../../__generated__/pgeplus-types';
import { VirtualAccountType } from '../../../constants/customEnum';
import useERApplicationApi from '../../../hooks/pge-plus/useERApplicationApi';
import useEVApplicationApi from '../../../hooks/pge-plus/useEVApplicationApi';
import useUtil from '../../../hooks/pge-plus/useUtil';
import useWelcomeBackApi from '../../../hooks/pge-plus/useWelcomeBackApi';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import useAccountDetailList from '../../../hooks/useAccountDetailList';
import useContentMgmt from '../../../hooks/useContentMgmt';
import useMainCustomerQuery from '../../../hooks/useMainCustomerQuery';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import { useRebateOnlyFormState } from '../../../providers/RebateOnlyFormStateProvider';
import ROUTES from '../../../routes';
import TodIcon from '../../../static/Icon-TOD.svg';
import RightArrow from '../../../static/right-arrow-black.svg';
import colors from '../../../themes/main-colors';
import AccountDropdown from '../../account-dropdown';
import {
  getAccountsList,
  getSelectedAccount,
} from '../../account-dropdown/queries';
import useAccountListHeader from '../../account-summary/multi-accounts/useAccountListHeader';
import Backdrop from '../../backdrop';
import RestartApplicationPopup from '../../pge-plus-common/RestartApplicationPopup';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { IneligibilityTypeNumber } from '../Ineligible';
import { CreateANewApplication } from './CreateANewApplication';
import WelcomeBackERApplication from './ERApplication';
import WelcomeBackEVApplication from './EVApplication';
import WelcomeBackConsultationRequest from './WelcomeBackConsultationRequest';
import { InactiveApplication } from './InactiveApplication';
import { InactiveConsultationRequest } from './InactiveConsultationRequest';
import useAuthQuery from '../../../hooks/useAuthQuery';
import { AccountDetail } from '../../../__generated__/pge-types';

interface WelcomeBackProps {
  children?: React.ReactNode;
  path: string;
}

export const WelcomeBack: React.FC<WelcomeBackProps> = props => {
  const { state } = useLocation();
  const theme = useTheme();
  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_WELCOME_BACK,
  );
  const { customer } = useAccountCustomer();
  const newApplications = new Set<ApplicationType>([ApplicationType.Ev]);
  const { refetch: checkMainCustomer } = useMainCustomerQuery();
  const { accountListParams } = useAccountListHeader();
  const { accountNumber, setSelectedAccountParams } = useSelectedAccountParams({
    ignoreNoAccounts: true,
  });
  const { accounts } = useAccountDetailList(
    getAccountsList,
    VirtualAccountType.ALL_ACCTS,
    {
      accountListParams,
    },
  );
  const urlParams: any = new URLSearchParams(window.location.search);
  const accountIdQueryParam = urlParams.get('accountId');
  //@ts-ignore
  const passedAccountId = state?.accountNumber;
  const accountId = passedAccountId || accountIdQueryParam || accountNumber;

  const { data: accountData } = useAuthQuery<{
    getAccountDetails: AccountDetail[];
  }>(getSelectedAccount, {
    variables: {
      params: {
        accountNumberList: [
          {
            accountNumber: accountId,
            encryptedPersonId: customer?.encryptedPersonId,
          },
        ],
      },
    },
    skip: !accountId || !customer?.encryptedPersonId,
  });

  const accountDetail = accountData?.getAccountDetails?.[0];

  const {
    getApplicationsByAccountNo,
    getHvacConsultationApplications,
  } = useWelcomeBackApi();

  const [loading, setLoading] = useState<{
    page: boolean;
    action: boolean;
  }>({
    page: true,
    action: false,
  });

  const [applications, setApplications] = useState<{
    active?: { data: Application; currentActiveStep?: number }[];
    inActive?: Application[];
    closed?: { data: Application; currentActiveStep?: number }[];
    new?: ApplicationType[];
  }>({
    new: Array.from(newApplications),
  });

  const [consultationRequests, setConsultationRequests] = useState<{
    active?: { data: Request; currentActiveStep?: number }[];
    inActive?: { data: Request; currentActiveStep?: number }[];
    closed?: { data: Request; currentActiveStep?: number }[];
  }>({
    active: [],
    inActive: [],
    closed: [],
  });

  const [
    restartApplicationModalOpen,
    setRestartApplicationModalOpen,
  ] = useState(false);

  const [discardApplicationId, setDiscardApplicationId] = useState<string>('');

  const {
    initFormStateAndNavigate: initEVFormStateAndNavigate,
    updateFormState,
  } = useEVChargerFormState();

  const {
    initFormStateAndNavigate: initERFormStateAndNavigate,
  } = useRebateOnlyFormState();

  const {
    getApplicationDetails: getApplicationERDetails,
  } = useERApplicationApi();

  const {
    cancelApplication,
    getApplicationDetails: getApplicationEVDetails,
  } = useEVApplicationApi();

  const sortApplicationsByCreatedDate = (apps: Application[]) =>
    apps?.sort((a: Application, b: Application) =>
      b.createdAt.localeCompare(a.createdAt),
    );

  const sortRequestsByCreatedDate = (apps: Request[]) =>
    apps?.sort((a: Request, b: Request) =>
      b.createdAt.localeCompare(a.createdAt),
    );

  const fetchApplicationsForAnAccount = wrapWithLoader(async () => {
    setLoading({
      page: true,
      action: false,
    });
    if (accountNumber && accounts.length > 0) {
      try {
        setApplications({
          new: [ApplicationType.Ev],
        });
        const result = await checkMainCustomer({
          params: {
            accountNumberList: [
              {
                accountNumber: accountNumber,
                encryptedPersonId: customer?.encryptedPersonId,
              },
            ],
            encryptedPersonId: customer?.encryptedPersonId,
          },
        });
        if (
          !result.loading &&
          !result.errors &&
          Object.keys(result?.data?.getAccountDetails).length > 0 &&
          !(
            result.data.getAccountDetails[0].relationType === 'MAIN' ||
            result.data.getAccountDetails[0].relationType === 'COAPP'
          )
        ) {
          void navigate(ROUTES.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
            state: {
              IneligibilityCode: IneligibilityReasonCode.IneligibleC2M,
              IneligibilityNumber:
                IneligibilityTypeNumber.NOT_MAIN_OR_CO_CUSTOMER,
              fromPage: ROUTES.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
            },
          });
          return;
        }

        const results = await Promise.allSettled([
          getApplicationsByAccountNo({
            accountId: [accountNumber],
          }),
          getHvacConsultationApplications({
            premiseId: [
              accountDetail?.premiseInfo?.find(
                premise => premise?.encryptedPremiseId,
              )?.encryptedPremiseId,
            ],
            status: [
              RequestStatus.Created,
              RequestStatus.InProgress,
              RequestStatus.Completed,
              RequestStatus.Canceled,
            ],
            category: [
              RequestCategory.EvConsultation,
              RequestCategory.HvacConsultation,
            ],
          }),
        ]);

        if (
          results[1].status === 'fulfilled' &&
          results[1].value?.data?.getRequests.requests
        ) {
          const requests = results[1].value?.data?.getRequests.requests;

          const _activeRequests = requests?.filter(
            request1 =>
              request1.status === RequestStatus.Created ||
              request1.status === RequestStatus.InProgress,
          );
          const activeRequests = sortRequestsByCreatedDate(
            _activeRequests,
          )?.map(request => ({
            data: request,
            currentActiveStep: getCurrentActiveStepForConsultationRequest(
              request,
            ),
          }));

          const _inActiveRequests = requests?.filter(
            request => request.status === RequestStatus.Canceled,
          );
          const inActiveRequests = sortRequestsByCreatedDate(
            _inActiveRequests,
          )?.map(request => ({
            data: request,
            currentActiveStep: getCurrentActiveStepForConsultationRequest(
              request,
            ),
          }));

          const _closedRequests = requests?.filter(
            request => request.status === RequestStatus.Completed,
          );
          const closedRequests = sortRequestsByCreatedDate(
            _closedRequests,
          )?.map(req => ({
            data: req,
            currentActiveStep: getCurrentActiveStepForConsultationRequest(req),
          }));

          setConsultationRequests(prev => ({
            ...prev,
            active: activeRequests,
            inActive: inActiveRequests,
            closed: closedRequests,
          }));
        } else {
          setConsultationRequests(prev => ({
            ...prev,
            active: [],
            inActive: [],
            closed: [],
          }));
        }

        if (results[0].status === 'fulfilled') {
          const { data } = results[0].value;

          setLoading({
            page: false,
            action: false,
          });

          if (data?.getApplications?.applications?.length > 0) {
            const apps = data?.getApplications?.applications;

            const _activeApplications = apps?.filter(
              app =>
                app.status === ApplicationStatus.Created ||
                app.status === ApplicationStatus.InProgress ||
                app.status === ApplicationStatus.Submitted ||
                app.status === ApplicationStatus.Servicing ||
                (app.status === ApplicationStatus.Completed &&
                  app?.job &&
                  app.job?.payOutOfPocket?.charges?.isBillGenerated &&
                  (app.job?.payOutOfPocket?.charges?.amountDue ?? 0) > 0) ||
                //TODO: Need to re-visit the logic for completed apps after introduction of new Service_Completed app status
                (app.status === ApplicationStatus.Completed &&
                  app?.job &&
                  !app?.job?.payOutOfPocket?.charges?.isBillGenerated &&
                  app.status === ApplicationStatus.Completed &&
                  !app?.job?.isLegacyJob) ||
                  (app.status === ApplicationStatus.Canceled &&
                  app?.job &&
                  app?.job?.status === JobStatus.Expired &&
                  app?.job?.payOutOfPocket?.charges?.isBillGenerated === true &&
                  app?.job?.payOutOfPocket?.charges?.amountDue! > 0)
            );
            const activeApplications = sortApplicationsByCreatedDate(
              _activeApplications,
            )?.map(app => ({
              data: app,
              currentActiveStep: getCurrentActiveStep(app),
            }));

            activeApplications.forEach(app => {
              const appType = app.data.applicationType;
              if (
                appType === ApplicationType.Ev ||
                appType === ApplicationType.Er
              ) {
                newApplications.delete(ApplicationType.Ev);
                newApplications.delete(ApplicationType.Er);
              } else {
                newApplications.delete(appType);
              }
            });

            const _inActiveApplications = apps?.filter(
              app =>
                (app.status === ApplicationStatus.Canceled &&
                  app?.job?.status === JobStatus.Expired &&
                  !(app?.job?.payOutOfPocket?.charges?.isBillGenerated === true) &&
                  !(app?.job?.payOutOfPocket?.charges?.amountDue! > 0)) ||
                app.status === ApplicationStatus.Expired ||
                app?.job?.quote?.status === QuoteStatus.Rejected,
            );
            const inActiveApplications = sortApplicationsByCreatedDate(
              _inActiveApplications,
            );

            const _closedApplications = apps?.filter(
              app =>
                (app.status === ApplicationStatus.Completed &&
                  ((app.job &&
                    app.job?.payOutOfPocket?.charges?.isBillGenerated &&
                    (app.job?.payOutOfPocket?.charges.amountDue ?? 0) <= 0) ||
                    !app.job)) || // For ER apps checking if job is present
                (app?.job?.isLegacyJob &&
                  app.status === ApplicationStatus.Completed),
            );

            const closedApplications = sortApplicationsByCreatedDate(
              _closedApplications,
            )?.map(app => ({
              data: app,
              currentActiveStep: getCurrentActiveStep(app),
            }));

            setApplications(prev => ({
              ...prev,
              active: activeApplications,
              inActive: inActiveApplications,
              closed: closedApplications,
              new: Array.from(newApplications),
            }));
          }
        } else {
          setApplications(prev => ({
            ...prev,
            active: [],
            inActive: [],
            closed: [],
            new: Array.from(newApplications),
          }));
        }
      } catch (err) {
        setLoading({
          page: false,
          action: false,
        });
        setErrorNotification(true);
        setApplications({});
      }
    }
  });

  const getCurrentActiveStepForConsultationRequest = (application: Request) => {
    if (
      application.status === RequestStatus.Created ||
      (application.status === RequestStatus.InProgress &&
        Boolean(
          application?.contactAttempts?.filter(
            contactAttempt =>
              contactAttempt?.status !== ContactStatus.Successful,
          )?.length,
        ))
    ) {
      return 2;
    }
    if (application.status === RequestStatus.InProgress) {
      return 3;
    }
    if (
      application.status === RequestStatus.Completed ||
      application.status === RequestStatus.Canceled
    ) {
      return 4;
    }
    return undefined;
  };

  const getCurrentActiveStep = (application: Application) => {
    if (application?.job) {
      if (
        (application?.job?.status === JobStatus.ServiceCompleted ||
          application?.job?.status === JobStatus.Completed) &&
        application?.job?.payOutOfPocket?.charges?.isBillGenerated === true &&
        application?.job?.payOutOfPocket?.charges?.billId !== null
      ) {
        return 5;
      }
      if (
        application?.job?.status === JobStatus.Expired  &&
        application?.job?.payOutOfPocket?.charges?.isBillGenerated === true &&
        application?.job?.payOutOfPocket?.charges?.amountDue > 0
      ) {
        return 5;
      }
    }
    if (application?.job?.quote) {
      if (
        (application?.job?.quote?.status === QuoteStatus.FinancingConfirmed ||
          application?.job?.quote?.status === QuoteStatus.Accepted) &&
        !application?.job?.payOutOfPocket?.charges?.isBillGenerated
      ) {
        return 4;
      }
      if (application?.job?.quote?.status === QuoteStatus.Created) {
        return 3;
      }
      if (application?.job?.quote?.status === QuoteStatus.Rejected) {
        return undefined;
      }
    }

    if (application?.job?.status) {
      if (
        application?.job?.status === JobStatus.Created ||
        application?.job?.status === JobStatus.Accepted ||
        application?.job?.status === JobStatus.Rejected
      ) {
        return 2;
      }
      if (
        application?.job?.status === JobStatus.Canceled ||
        application?.job?.status === JobStatus.ServiceCompleted ||
        application?.job?.status === JobStatus.Completed
      ) {
        return undefined;
      }
    }

    if (application?.status) {
      if (
        application?.status === ApplicationStatus.Created ||
        application?.status === ApplicationStatus.InProgress
      ) {
        return 1;
      }
      if (application?.status === ApplicationStatus.Submitted) {
        return 2;
      }

      if (
        application?.applicationType === ApplicationType.Er &&
        application?.status === ApplicationStatus.Completed
      ) {
        return 2;
      }

      if (
        application?.status === ApplicationStatus.Expired ||
        application?.status === ApplicationStatus.Rejected
      ) {
        return undefined;
      }
    }
    return undefined;
  };

  const handleResumeApplication = wrapWithLoader(async (app: Application) => {
    if (app.applicationType === ApplicationType.Ev) {
      const { data } = await getApplicationEVDetails({
        applicationId: app.applicationNo,
      });
      if (data?.getApplication?.application) {
        initEVFormStateAndNavigate(
          data.getApplication.application as ApplicationEv,
        );
      }
    } else if (app.applicationType === ApplicationType.Er) {
      const { data } = await getApplicationERDetails({
        applicationId: app.applicationNo,
      });
      if (data?.getApplication?.application) {
        initERFormStateAndNavigate(
          data.getApplication.application as ApplicationEr,
        );
      }
    }
  });

  const handleOnClickRestartApplication = (id: string) => {
    setRestartApplicationModalOpen(true);
    setDiscardApplicationId(id);
  };

  const discardApplication = wrapWithLoader(async (id: string) => {
    try {
      setLoading({
        page: false,
        action: true,
      });

      // Cancel application mutation is same for both EV and ER
      const response = await cancelApplication({
        applicationId: id,
        reason: content.get('EV_CANCEL_APPLICATION_REASON_USER_RESTART') || '',
      });
      setLoading({
        page: false,
        action: false,
      });
      if (response?.data?.cancelApplication.canceled) {
        void fetchApplicationsForAnAccount();
      } else {
        setErrorNotification(true);
      }
    } catch (e) {
      setLoading({
        page: false,
        action: false,
      });
      setErrorNotification(true);
    }
  });

  const setEVFormData = (app: Application) => {
    if (app.applicationType === ApplicationType.Ev) {
      updateFormState('application', app as ApplicationEv);
    }
  };

  const handleClickGoToQuote = (app: Application) => {
    setEVFormData(app);
    void navigate(ROUTES.PGE_PLUS_EV_CHARGERS_FINAL_QUOTE);
  };

  const handleViewAcceptedQuote = (app: Application) => {
    setEVFormData(app);
    void navigate(ROUTES.PGE_PLUS_EV_CHARGERS_VIEW_EV_QUOTE);
  };

  const handleClickViewApplicationSummary = wrapWithLoader(
    async (app: Application) => {
      if (app.applicationType === ApplicationType.Ev) {
        const { data } = await getApplicationEVDetails({
          applicationId: app.applicationNo,
        });
        if (data?.getApplication?.application) {
          updateFormState(
            'application',
            data?.getApplication?.application as ApplicationEv,
          );
          void navigate(
            ROUTES.PGE_PLUS_EV_CHARGERS_VIEW_EV_APPLICATION_SUMMARY,
          );
        }
      } else if (app.applicationType === ApplicationType.Er) {
        // TODO: This scenario will be handled post-POP Rocks release
      }
    },
  );

  useEffect(() => {
    if (accountDetail) {
      void fetchApplicationsForAnAccount();
    }
  }, [accountNumber, accounts.length, accountDetail]);

  useEffect(() => {
    if (accountDetail && !(typeof window === 'undefined')) {
      window.history.pushState(
        {},
        document.title,
        ROUTES.PGE_PLUS_EV_CHARGERS_WELCOME_BACK,
      );
      window.history.replaceState({}, '');
      setSelectedAccountParams(VirtualAccountType.ALL_ACCTS, accountDetail);
    }
  }, [accountDetail]);

  if (loading.page) {
    return <Backdrop forceOpen />;
  }

  return (
    <React.Fragment>
      <Box
        css={{
          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
            marginBottom: '129px',
          },
        }}
      >
        {loading.action && <Backdrop forceOpen />}

        <Box css={{ marginBottom: 3 }}>
          <Box>
            <Typography
              component="h1"
              variant="h1"
              css={{
                fontFamily: 'Forma-DJR-Display',
                color: colors.noirBlur,
                letterSpacing: '0.72px',
                lineHeight: '43px !important',
                textAlign: 'center',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  textAlign: 'start',
                  fontSize: '24px',
                },
              }}
            >
              {content.get('WELCOME_TO_PGE_PLUS_MODAL_TITLE')}
            </Typography>
          </Box>
          <Box>
            <Typography
              component="h3"
              variant="h3"
              css={{
                fontFamily: 'Untitled-Sans',
                color: colors.noirBlur,
                letterSpacing: 0,
                fontWeight: 'normal',
                fontSize: '1.25rem',
                margin: '30px 0',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  margin: '24px 0',
                  fontSize: '1rem',
                },
              }}
            >
              {content.get('HERE_ARE_YOUR_NEXT_STEPS')}
            </Typography>
          </Box>
        </Box>

        <Box
          css={{
            marginBottom: theme.spacing(4),
          }}
        >
          <AccountDropdown />
        </Box>

        {/* Active Application Cards */}
        <Box>
          {applications?.active?.map(app => {
            if (
              app.data.applicationType === ApplicationType.Ev &&
              app.currentActiveStep
            ) {
              return (
                <WelcomeBackEVApplication
                  application={app.data as ApplicationEv}
                  activeStep={app.currentActiveStep}
                  handleRestartApplication={() =>
                    handleOnClickRestartApplication(app.data.id)
                  }
                  handleResumeApplication={() =>
                    handleResumeApplication(app.data)
                  }
                  handleClickGoToQuote={() => handleClickGoToQuote(app.data)}
                  handleViewApplication={() =>
                    handleClickViewApplicationSummary(app.data)
                  }
                  handleViewAcceptedQuote={() =>
                    handleViewAcceptedQuote(app.data)
                  }
                  content={content}
                />
              );
            }

            if (
              app.data.applicationType === ApplicationType.Er &&
              app.currentActiveStep
            ) {
              return (
                <WelcomeBackERApplication
                  application={app.data as ApplicationEr}
                  activeStep={app.currentActiveStep}
                  handleRestartApplication={() =>
                    handleOnClickRestartApplication(app.data.id)
                  }
                  handleResumeApplication={() =>
                    handleResumeApplication(app.data)
                  }
                  handleViewApplication={() =>
                    handleClickViewApplicationSummary(app.data)
                  }
                  content={content}
                />
              );
            }
          })}

          {consultationRequests?.active?.map(consultationRequest => {
            if (consultationRequest?.currentActiveStep) {
              return (
                <WelcomeBackConsultationRequest
                  application={consultationRequest?.data}
                  activeStep={consultationRequest?.currentActiveStep}
                  content={content}
                />
              );
            }
          })}
        </Box>

        {/* Start A New Application Cards */}
        {applications?.new?.map(appType => (
          <CreateANewApplication type={appType} content={content} />
        ))}

        {/* Completed Application Cards */}
        <Box
          css={{
            marginTop: theme.spacing(4),
          }}
        >
          {applications?.closed?.map(app => {
            if (
              app.data.applicationType === ApplicationType.Ev &&
              app.currentActiveStep
            ) {
              return (
                <WelcomeBackEVApplication
                  application={app.data as ApplicationEv}
                  activeStep={app.currentActiveStep}
                  handleRestartApplication={() =>
                    handleOnClickRestartApplication(app.data.id)
                  }
                  handleResumeApplication={() =>
                    handleResumeApplication(app.data)
                  }
                  handleClickGoToQuote={() => handleClickGoToQuote(app.data)}
                  handleViewApplication={() =>
                    handleClickViewApplicationSummary(app.data)
                  }
                  handleViewAcceptedQuote={() =>
                    handleViewAcceptedQuote(app.data)
                  }
                  content={content}
                  isClosed={true}
                />
              );
            }

            if (
              app.data.applicationType === ApplicationType.Er &&
              app.currentActiveStep
            ) {
              return (
                <WelcomeBackERApplication
                  application={app.data as ApplicationEr}
                  activeStep={app.currentActiveStep}
                  handleRestartApplication={() =>
                    handleOnClickRestartApplication(app.data.id)
                  }
                  handleResumeApplication={() =>
                    handleResumeApplication(app.data)
                  }
                  handleViewApplication={() =>
                    handleClickViewApplicationSummary(app.data)
                  }
                  content={content}
                  isClosed={true}
                />
              );
            }
          })}
        </Box>
        <Box
          css={{
            marginTop: theme.spacing(4),
          }}
        >
          {consultationRequests?.closed?.map(consultationRequest => {
            if (consultationRequest?.currentActiveStep) {
              return (
                <WelcomeBackConsultationRequest
                  application={consultationRequest?.data}
                  activeStep={consultationRequest?.currentActiveStep}
                  content={content}
                />
              );
            }
          })}
        </Box>
        <Box
          css={{
            marginTop: theme.spacing(4),
          }}
        >
          {/* Inactive Application Cards */}
          {applications?.inActive?.map(app => (
            <InactiveApplication
              type={app.applicationType}
              applicationNumber={app.applicationNo}
              applicationStartDate={app.createdAt}
              handleViewApplication={() =>
                handleClickViewApplicationSummary(app)
              }
              content={content}
            />
          ))}
        </Box>
        <Box
          css={{
            marginTop: theme.spacing(4),
          }}
        >
          {consultationRequests?.inActive?.map(consultationRequest => {
            if (consultationRequest?.currentActiveStep) {
              return (
                <InactiveConsultationRequest
                  consultationRequest={consultationRequest?.data}
                  content={content}
                  activeStep={consultationRequest?.currentActiveStep}
                />
              );
            }
          })}
        </Box>
        <Divider
          css={{
            background: colors.lightGray1,
            margin: '50px 0',
            [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
              margin: '30px 0',
            },
          }}
        />

        <Box>
          <Typography
            component="h2"
            variant="h2"
            css={{
              fontFamily: 'Forma-DJR-Display',
              color: colors.noirBlur,
              letterSpacing: '0.48px',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              textAlign: 'center',
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                fontSize: '1.25rem',
                textAlign: 'start',
              },
            }}
          >
            {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_WAYS_TO_SAVE')}
          </Typography>
          <a
            href={ROUTES.TIMEOFDAY}
            target="_blank"
            rel="noopener"
            style={{
              textDecoration: 'none',
              borderBottom: 'none',
            }}
          >
            <Box
              css={{
                marginTop: '30px',
                backgroundColor: colors.white,
                border: `1px solid ${colors.sparkBlue}`,
                padding: '20px',
                display: 'flex',
                [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  marginTop: '20px',
                },
              }}
            >
              <Box css={{ marginRight: '1rem' }}>
                <TodIcon />
              </Box>
              <Box>
                <Box>
                  <Box
                    css={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      component="h2"
                      variant="h2"
                      css={{
                        fontFamily: 'Forma-DJR-Display',
                        color: colors.sparkBlue,
                        letterSpacing: '0.48px',
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        marginBottom: '11px',
                        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                          fontSize: '1.25rem',
                        },
                      }}
                    >
                      {content.get(
                        'INSTALLATION_CUSTOMER_WELCOME_BACK_TOD_HEADER',
                      )}
                    </Typography>
                    <RightArrow />
                  </Box>
                </Box>
                <Typography
                  component="p"
                  css={{
                    fontFamily: 'Untitled-Sans',
                    color: colors.noirBlur,
                    letterSpacing: '0px',
                    lineHeight: '26px',
                    fontSize: '1.25rem',
                    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                    },
                  }}
                >
                  {content.get('INSTALLATION_CUSTOMER_WELCOME_BACK_TOD_TEXT')}
                </Typography>
              </Box>
            </Box>
          </a>
        </Box>
      </Box>
      <RestartApplicationPopup
        open={restartApplicationModalOpen}
        content={content}
        onClose={() => setRestartApplicationModalOpen(false)}
        onDiscardApplication={() => {
          setRestartApplicationModalOpen(false);
          void discardApplication(discardApplicationId);
        }}
      />
    </React.Fragment>
  );
};
